import React, { useState, useEffect } from "react";
import T from '../../components/common/T';
import { usePurchaseOrder } from "../../context/PurchaseOrderContext";
import { useVendors } from "../../context/VendorContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Price from "../../components/common/Price";
import { UserCircleIcon, ClipboardDocumentCheckIcon, TruckIcon } from "@heroicons/react/24/outline";

export default function VendorPortal() {
  const { vendors } = useVendors();
  const { getRFQsForVendor, submitQuoteFromRFQ, purchaseOrders, updatePOStatus } = usePurchaseOrder();

  // Login State
  const [currentVendorId, setCurrentVendorId] = useState("");
  const currentVendor = vendors.find(v => String(v.id) === String(currentVendorId));

  // --- 1. RFQ LOGIC ---
  const [myRFQs, setMyRFQs] = useState([]);
  const [priceInputs, setPriceInputs] = useState({});

  useEffect(() => {
    const loadData = async () => {
      if (currentVendorId) {
        const data = await getRFQsForVendor(currentVendorId);
        setMyRFQs(Array.isArray(data) ? data : []);
      } else {
        setMyRFQs([]);
      }
    };
    loadData();
  }, [currentVendorId, getRFQsForVendor]);

  const handleSubmitQuote = async (rfqId) => {
      const price = priceInputs[rfqId];
      if(!price) return toast.error("Enter a price first.");
      const success = await submitQuoteFromRFQ(rfqId, price);
      if(success) {
          toast.success("Quote Submitted!");
          const updated = await getRFQsForVendor(currentVendorId);
          setMyRFQs(updated);
      }
  };

  // --- 2. PO LOGIC (🟢 FIXED) ---
  // We use a local state 'visibleOrders' to handle the UI update instantly
  const [visibleOrders, setVisibleOrders] = useState([]);

  useEffect(() => {
    if(currentVendorId && purchaseOrders.length > 0) {
        // Filter orders for this vendor
        const filtered = purchaseOrders.filter(po => 
            (String(po.vendorId) === String(currentVendorId) || po.vendorName === currentVendor?.vendorName) && 
            po.status === "Ordered"
        );
        setVisibleOrders(filtered);
    }
  }, [purchaseOrders, currentVendorId, currentVendor]);

  const handleDispatchOrder = async (poId) => {
    if(!window.confirm("Confirm dispatch of this order?")) return;

    // 🟢 1. OPTIMISTIC UPDATE: Remove from screen immediately
    setVisibleOrders(prev => prev.filter(po => po.id !== poId));

    // 🟢 2. API CALL
    const success = await updatePOStatus(poId, "Dispatched");
    if(success) {
        toast.success("Order marked as Dispatched! Admin notified.");
    } else {
        toast.error("Failed. Please try again.");
        // Revert if failed (optional, but good practice)
    }
  };

  if (!currentVendorId) {
      return (
          <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
              <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
                  <h1 className="text-base font-bold text-gray-800 mb-6"><T>Vendor Portal Login</T></h1>
                  <select className="w-full border p-3 rounded mb-4" onChange={(e) => setCurrentVendorId(e.target.value)}>
                      <option value=""><T>-- Select Vendor Profile --</T></option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.name || v.vendorName}</option>)}
                  </select>
              </div>
          </div>
      )
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      
      {/* HEADER */}
      <div className="bg-gray-900 text-white px-6 py-4 flex justify-between items-center shadow-md sticky top-0 z-20">
          <div className="flex items-center gap-3">
              <div className="bg-gray-700 p-2 rounded-full"><UserCircleIcon className="w-6 h-6"/></div>
              <div>
                  <h1 className="font-bold text-base">{currentVendor?.name || currentVendor?.vendorName}</h1>
                  <p className="text-xs text-gray-400"><T>Vendor Portal Access</T></p>
              </div>
          </div>
          <button onClick={() => setCurrentVendorId("")} className="text-sm text-gray-300 hover:text-white"><T>Logout</T></button>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* LEFT: RFQs */}
          <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <ClipboardDocumentCheckIcon className="w-7 h-7 text-purple-600"/>
                  Requests for Quotation
              </h2>
              {myRFQs.length === 0 ? (
                  <div className="bg-white p-12 rounded-lg text-center border border-dashed text-gray-500"><T>No pending requests.</T></div>
              ) : (
                  <div className="grid gap-4">
                      {myRFQs.map(rfq => (
                          <div key={rfq.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                              <div className="flex justify-between items-start mb-4">
                                  <div>
                                      <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded mb-1 inline-block">{rfq.prNumber}</span>
                                      <h3 className="text-base font-bold text-gray-800">{rfq.materialName}</h3>
                                      <p className="text-gray-600 text-sm"><T>Qty: </T><b>{rfq.quantity}</b></p>
                                  </div>
                                  <span className="text-xs text-gray-400">{new Date(rfq.created_at || Date.now()).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                  <span className="text-sm font-bold text-gray-600"><T>Your Offer: ₹</T></span>
                                  <input type="number" className="border p-2 rounded w-32" placeholder="0.00" onChange={(e) => setPriceInputs({...priceInputs, [rfq.id]: e.target.value})} />
                                  <button onClick={() => handleSubmitQuote(rfq.id)} className="bg-purple-600 text-white px-4 py-2 rounded font-bold text-sm"><T>Submit</T></button>
                              </div>
                          </div>
                      ))}
                  </div>
              )}
          </div>

          {/* RIGHT: POs (Dispatch) */}
          <div>
            <h2 className="text-base font-bold mb-6 flex items-center gap-2 text-gray-800">
                <TruckIcon className="w-7 h-7 text-green-600"/>
                Active Orders (To Dispatch)
            </h2>

            {visibleOrders.length === 0 ? (
                <div className="text-center py-10 text-gray-400 bg-white rounded-lg border border-dashed"><T>No active orders to dispatch.</T></div>
            ) : (
                <div className="space-y-4">
                    {visibleOrders.map(po => (
                        <div key={po.id} className="bg-white border border-green-200 p-5 rounded-xl shadow-sm relative">
                             <div className="flex justify-between items-start mb-3">
                                <div>
                                    <h4 className="font-bold text-green-900 text-base">{po.poNumber}</h4>
                                    {/* 🟢 FIX: Item Name Display */}
                                    <p className="text-sm text-gray-700 font-medium">{po.materialName} <span className="text-gray-400">×</span> {po.quantity}</p>
                                </div>
                                <div className="text-right">
                                    {/* 🟢 FIX: Price Formatting */}
                                    <span className="block font-bold text-xl text-green-900"><Price amount={Number(po.totalPrice)} /></span>
                                    <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full"><T>Ordered</T></span>
                                </div>
                             </div>
                             
                             <button 
                                className="w-full bg-green-600 text-white py-3 rounded-lg font-bold shadow-md hover:bg-green-700 transition flex justify-center items-center gap-2"
                                onClick={() => handleDispatchOrder(po.id)}
                             >
                                <TruckIcon className="w-5 h-5" />
                                Mark as Dispatched
                             </button>
                        </div>
                    ))}
                </div>
            )}
        </div>

      </div>
    </div>
  );
}